import React, { useState } from 'react';
import { Box, Typography, Avatar, Stack, Button } from '@mui/material';
import { SPACING, RADIUS } from '../../../theme/theme';
import { useImageLightbox } from '../../Feedback/ImageLightbox';

interface AppDetailsProps {
  title: string;
  images: { url: string }[];
  description: string;
  additionalInfo: string;
}

const AppDetailsLeft: React.FC<AppDetailsProps> = ({
  title,
  images,
  description,
  additionalInfo,
}) => {
  const lightbox = useImageLightbox();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showFullAdditionalInfo, setShowFullAdditionalInfo] = useState(false);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',

        justifyContent: 'center',
        alignItems: 'center',
        gap: SPACING.sm,
        paddingLeft: SPACING.md,
        paddingRight: SPACING.md,
        paddingTop: 0,
        paddingBottom: SPACING.md,
      }}
    >
      {/* Images Section */}
      <Box
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: SPACING.sm,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            alignSelf: 'stretch',
            textAlign: 'right',
            color: '#60646E',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '20px',
          }}
        >
          {title}
        </Typography>
        <Stack
          direction="row"
          sx={{
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            display: 'flex',
            gap: SPACING.sm,
            padding: SPACING.xs,
            width: '100%',
            maxWidth: '100%',
            whiteSpace: 'nowrap',
          }}
        >
          {images.map((src, index) => (
            <Avatar
              key={index}
              src={src.url}
              variant="rounded"
              {...lightbox.triggerProps(src.url, `${title} ${index + 1}`)}
              sx={{
                width: 68,
                height: 120,
                borderRadius: `${RADIUS.sm}px`,
                minWidth: 68,
                cursor: 'zoom-in',
              }}
            />
          ))}
        </Stack>
      </Box>

      {/* Description Section */}
      <Box
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',

          gap: SPACING.xs,
          maxHeight: 300,
          overflowY: 'auto',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            alignSelf: 'stretch',
            textAlign: 'right',
            color: '#60646E',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '20px',
          }}
        >
          توضیحات
        </Typography>
        <Typography
          variant="body2"
          sx={{
            alignSelf: 'stretch',
            textAlign: 'justify',
            color: '#25282E',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '20px',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: showFullDescription ? 'none' : 4,
            overflow: showFullDescription ? 'visible' : 'hidden',
          }}
        >
          {description}
        </Typography>
        {description && description.length > 70 && (
          <Button
            variant="text"
            sx={{ color: '#1976d2', fontSize: 12 }}
            onClick={() => setShowFullDescription(!showFullDescription)}
          >
            {showFullDescription ? 'نمایش کمتر' : 'نمایش بیشتر'}
          </Button>
        )}
      </Box>

      {/* Additional Info Section */}
      {additionalInfo && (
        <Box
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: SPACING.xs,
            maxHeight: 300,
            overflowY: 'auto',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              alignSelf: 'stretch',
              textAlign: 'right',
              color: '#60646E',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '20px',
            }}
          >
            توضیحات تکمیلی
          </Typography>

          <Typography
            variant="body2"
            sx={{
              alignSelf: 'stretch',
              textAlign: 'justify',
              color: '#25282E',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: '20px',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: showFullAdditionalInfo ? 'none' : 8,
              overflow: showFullAdditionalInfo ? 'visible' : 'hidden',
            }}
          >
            {additionalInfo}
          </Typography>
          {additionalInfo && additionalInfo.length > 140 && (
            <Button
              variant="text"
              sx={{ color: '#1976d2', fontSize: 12 }}
              onClick={() => setShowFullAdditionalInfo(!showFullAdditionalInfo)}
            >
              {showFullAdditionalInfo ? 'نمایش کمتر' : 'نمایش بیشتر'}
            </Button>
          )}
        </Box>
      )}
      {lightbox.element}
    </Box>
  );
};

export default AppDetailsLeft;
